import './entity-playlists.system-administrator.screen.scss';
import {useState, MutableRefObject, useRef, useEffect, useContext} from 'react';
import {useParams, useRouteMatch} from 'react-router-dom';
import {Box} from '@mui/material';
import {PlaylistType} from '../../../../../../../backend/enums/playlist-type';
import {Playlist} from '../../../../../../../backend/interfaces/playlist';
import {ContentPage} from '../../../../../../../components/content-page/content-page';
import {PageHeader} from '../../../../../../../components/page-header/page-header';
import {PaginatedTable} from '../../../../../../../components/paginated-table/paginated-table';
import {SelectInput} from '../../../../../../../components/select-input/select-input';
import {usePaginatedTable} from '../../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../../i18n/translations';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../../../../../../repositories/playlists';
import {playlistTypes} from '../../../../../../../utils/data/playlist-types';
import {useTranslation} from 'react-i18next';
import {AlertModal} from '../../../../../../../components/alert-modal/alert-modal';
import {OfficeContext} from '../../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../../contexts/office/types';
import {SnackBarContext} from '../../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../../contexts/snack-bar/types';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  State = 'state',
}

export default function EntityPlaylistsSystemAdministratorOfficeScreen() {
  // Get params
  let {id}: {id?: string} = useParams();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Hold state
  const [playlistType, setPlaylistType] = useState<PlaylistType>(PlaylistType.Karaoke);
  const [updatePlaylistDialogOpened, setUpdatePlaylistDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const playlistTypeRef: MutableRefObject<PlaylistType> = useRef(PlaylistType.Karaoke);
  const dataToUpdateRef: MutableRefObject<
    {playlistId: string; playlistName: string; playlistActiveFlag: boolean} | undefined
  > = useRef();

  // Translations
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Fetch playlists
  const handleGetPlaylists = async (limit: number, skip: number, search?: string) => {
    // Check id
    if (!id) {
      // Error
      throw new Error('Empty id.');
    }

    // Load playlists
    const result: {totalCount: number; data: Array<Playlist>} =
      await PlaylistsRepository.instance.getPlaylistsInEntityAsSystemAdministrator(
        id,
        playlistTypeRef.current,
        skip,
        limit,
        search,
        true
      );

    // Process data
    const processedData: PaginatedTableData<Playlist> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive === true ? true : false,
        avatarUrl: obj.imageUrl,
        toLink: url.charAt(url.length - 1) === '/' ? `${url}${obj.id}` : `${url}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            // The name is localized
            value: obj.name || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.entities.edit.playlists.state_value_true)
              : t(translations.entities.edit.playlists.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleUpdateItem,
  } = usePaginatedTable<Playlist>({
    fetchData: handleGetPlaylists,
  });

  // Change the playlist type
  const handleChangePlaylistType = (value: string) => {
    if (value !== playlistTypeRef.current) {
      // Update the playlist type
      setPlaylistType(value as PlaylistType);
    }
  };

  // Fetch the playlists again when the type changes
  useEffect(() => {
    if (playlistType !== playlistTypeRef.current) {
      // Update the playlist type
      playlistTypeRef.current = playlistType;

      // Fetch again
      handleApplyFilters(1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistType]);

  const handleAskUpdatePlaylistState = (playlistId: string, value: boolean) => {
    // Get playlist name
    const playlistName: string | undefined = data
      .find((obj) => obj.id === playlistId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set playlist info for later
    dataToUpdateRef.current = {
      playlistId: playlistId,
      playlistName: playlistName || '',
      playlistActiveFlag: value,
    };

    // Open modal
    setUpdatePlaylistDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdatePlaylistState = () => {
    // Close modal
    setUpdatePlaylistDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdatePlaylistState = () => {
    // Close modal
    setUpdatePlaylistDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdatePlaylistState();
  };

  const handleUpdatePlaylistState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const playlistId = dataToUpdateRef.current.playlistId;
      const value = dataToUpdateRef.current.playlistActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === playlistId)?.item;
      if (updatedItem) {
        updatedItem.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        playlistId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.entities.edit.playlists.state_value_true)
              : t(translations.entities.edit.playlists.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it will take too much time to update
      continueUpdatePlaylistState(id || '', playlistId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.entities.edit.playlists.errors.update_state_playlist_error_message),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the playlist. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdatePlaylistState = async (
    entityId: string,
    playlistId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await PlaylistsRepository.instance.updatePlaylistActiveStateForEntityAsSystemAdministrator(
        entityId,
        playlistId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === playlistId)?.item;
      if (updatedItem) {
        updatedItem.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        playlistId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.entities.edit.playlists.state_value_true)
              : t(translations.entities.edit.playlists.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(translations.entities.edit.playlists.errors.enable_playlist_error_message)
          : t(translations.entities.edit.playlists.errors.disable_playlist_error_message),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the playlist with id ${playlistId} to ${value} for entity with id ${entityId}.`,
        error
      );
    }
  };

  return (
    <ContentPage>
      <PageHeader
        showSearch={true}
        searchLabel={t(translations.entities.edit.playlists.search_input_placeholder)}
        searchText={currentSearch}
        searchOnChange={(text: string) => {
          // Update text search
          handleUpdateCurrentSearch(text);
        }}
        searchOnSubmit={() => {
          // Query backend about this
          handleApplyFilters(1, currentSearch);
        }}
        disableSearch={isLoading}
        filters={
          <Box className="entity-playlists-system-administrator-screen-select-input-container">
            <SelectInput
              className="entity-playlists-system-administrator-screen-select-input"
              label={t(translations.entities.edit.playlists.type_filter)}
              items={playlistTypes.map((obj) => {
                return {
                  value: obj.type,
                  label: t(obj.translation),
                };
              })}
              value={playlistType}
              handleChange={handleChangePlaylistType}
              inputSize="small"
            />
          </Box>
        }
      />
      <PaginatedTable
        avatar={{
          type: 'other',
        }}
        emptyTableMessage={t(translations.entities.edit.playlists.no_playlist)}
        columns={[
          {
            columnId: ColumnIdentifier.Name,
            title: t(translations.entities.edit.playlists.name_header),
            flexGrow: 2,
          },
          {
            columnId: ColumnIdentifier.State,
            title: t(translations.entities.edit.playlists.state_header),
            flexGrow: 1,
            button: {
              type: 'toggle',
              showColumnValue: true,
              toggle: {
                enabledValue: t(translations.entities.edit.playlists.state_value_true),
                handleClick: handleAskUpdatePlaylistState,
              },
            },
          },
        ]}
        rows={data}
        isLoading={isLoading}
        error={error}
        pagesCount={pagesCount}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        linkableRows={true}
      />
      <AlertModal
        id="confirm-disable-entity-playlist-state"
        opened={updatePlaylistDialogOpened.disableDialogOpened}
        title={t(translations.entities.edit.playlists.disable_playlist_dialog_title, {
          name: dataToUpdateRef.current?.playlistName || '',
        })}
        content={t(translations.entities.edit.playlists.disable_playlist_dialog_message, {
          name: officeContext.state.entityForm?.name || '',
        })}
        handleClose={handleDeclineUpdatePlaylistState}
        actions={[
          {
            label: t(
              translations.entities.edit.playlists.disable_playlist_dialog_action_yes_button
            ),
            type: 'destructive',
            handleClick: handleConfirmUpdatePlaylistState,
          },
          {
            label: t(translations.entities.edit.playlists.disable_playlist_dialog_action_no_button),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistState,
          },
        ]}
      />
      <AlertModal
        id="confirm-enable-entity-playlist-state"
        opened={updatePlaylistDialogOpened.enableDialogOpened}
        title={t(translations.entities.edit.playlists.enable_playlist_dialog_title, {
          name: dataToUpdateRef.current?.playlistName || '',
        })}
        content={t(translations.entities.edit.playlists.enable_playlist_dialog_message, {
          name: officeContext.state.entityForm?.name || '',
        })}
        handleClose={handleDeclineUpdatePlaylistState}
        actions={[
          {
            label: t(translations.entities.edit.playlists.enable_playlist_dialog_action_no_button),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistState,
          },
          {
            label: t(translations.entities.edit.playlists.enable_playlist_dialog_action_yes_button),
            type: 'nondestructive',
            handleClick: handleConfirmUpdatePlaylistState,
          },
        ]}
      />
    </ContentPage>
  );
}
